'use client'

import { useTranslation } from 'next-i18next/pages'
import { Box, Flex, Grid, Text, Heading, SimpleGrid } from '@chakra-ui/react'
import { DoubleColor } from '@/__generated__/graphql'
import JackpotIcon from '@/public/assets/images/double/double-jakcpot-icon-xl.png'
import Image from 'next/image'

import { simulate_gen_range } from '../pkg/rng_v1_web'
import { useEffect, useState } from 'react'
import { sha256 } from '@noble/hashes/sha256'
import { DoubleField } from '../../Double/atoms/DoubleField'
import { VerifyFormButton } from '../atoms/VerifyFormButton'
import { VerifyFormTextarea } from '../atoms/VerifyFormTextarea'
import { EmptyPlaceholder } from '../molecules/EmptyPlaceholder'
import { VerifyFormTitle } from '../molecules/VerifyFormTitle'
import { FormProvider, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { Section } from '../utils'
import { VerifyFormInput } from '../atoms/VerifyFormInput'
import { convertUInt32ToFloats, getUint32 } from '../pkg/rng_v2'
import { BlurEffect, GameItem } from '../../Runes/GameItem'
import { RUNSES } from '../../Runes/utils'

const schema = yup
  .object({
    gameId: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .required()
      .label('Game id')
      .min(1),
    signature: yup
      .string()
      .required()
      .label('Signature')
      .test('is-hex', '${path} is not hex', (value) => /^(0x)?[0-9a-fA-F]+$/.test(value || '')),
  })
  .required()

export const RunesVerify = () => {
  const { t } = useTranslation()
  const form = useForm({
    resolver: yupResolver(schema),
  })
  const [state, setState] = useState<{ result: number[] } | undefined>()

  useEffect(() => {
    const { unsubscribe } = form.watch(() => {
      setState(undefined)
    })
    return () => unsubscribe()
  }, [form.watch])

  const handleClick = async () => {
    await form.trigger()
    if (!form.formState.isValid) {
      setState(undefined)
      return
    }

    const signature = form.getValues('signature').replace('0x', '') as any

    const randomness = Buffer.from((await sha256(signature)) as any).toString('hex')

    setState({
      result: getUint32({
        serverKey: Buffer.from(randomness, 'hex'),
        userSeed: Buffer.from('runes-' + form.getValues('gameId')),
        count: 5,
      }).map((x: number) => Math.floor(convertUInt32ToFloats(x) * 7)),
    })
  }

  return (
    <Grid gap={5} gridTemplateColumns={{ base: '1fr', lg: '260px auto' }}>
      <Box p={5} rounded={'24px'} bg={'ninja.600'}>
        <FormProvider {...form}>
          <VerifyFormTitle mode="drng" />
          <VerifyFormInput label={t('verify.game-id')} placeholder={t('verify.enter-game-id')} name="gameId" />
          <VerifyFormTextarea
            label={t('verify.signature')}
            placeholder={t('verify.enter-signature')}
            name="signature"
          />
          <VerifyFormButton onClick={handleClick} />
        </FormProvider>
      </Box>
      <Box p={5} minH={{ base: '0', lg: '375px' }} rounded={'24px'} bg={'ninja.600'}>
        <Heading fontSize={'xl'} mb={{ base: 2, lg: 0 }}>
          {t('common.result')}
        </Heading>
        {!state ? <EmptyPlaceholder game={Section.RUNES} /> : <RunesVerifyResult result={state.result} />}
      </Box>
    </Grid>
  )
}

const RunesVerifyResult = ({ result }: { result: Array<number> }) => {
  const fillValue = [
    false,
    ...Array.from({ length: 7 }, (_, item: number) =>
      result.filter((x) => x !== null && x === item).length > 1 ? true : false
    ),
  ]

  return (
    <SimpleGrid h={'calc(100% - 24px)'} alignContent={'center'} columns={5} gap={2}>
      {result
        .filter((item) => item !== null)
        .map((item, id) => (
          <Box
            key={id}
            borderRadius={['24px', '24px', '40px']}
            boxShadow="0px -3.216px 9.647px rgba(255, 255, 255, 0.20) inset, 0px -9.647px 32.158px rgba(161, 164, 252, 0.32) inset"
          >
            <BlurEffect index={id} waiting={false}>
              <GameItem compact={true} item={RUNSES[item + 1]} isActive={fillValue[item + 1]} />
            </BlurEffect>
          </Box>
        ))}
    </SimpleGrid>
  )
}
